import { useMemo } from 'react'
import dayjs from 'dayjs'
import { mockInventoryItems, mockCategoryData, mockTurnoverData, mockExpiryData } from '../utils/mockReportsData'

export const useReportsData = (dateRange) => {
  const filteredItems = useMemo(() => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      return mockInventoryItems
    }

    const startDate = dateRange[0].startOf('day')
    const endDate = dateRange[1].endOf('day')

    return mockInventoryItems.filter((item) => {
      const itemDate = dayjs(item.expiryDate)
      return itemDate.isAfter(startDate) && itemDate.isBefore(endDate)
    })
  }, [dateRange])

  const overviewData = useMemo(() => {
    const totalValue = filteredItems.reduce((sum, item) => {
      const currentStock = item.currentStock || 0
      const unitPrice = item.unitPrice || 0
      return sum + currentStock * unitPrice
    }, 0)

    const itemsNeedingReorder = filteredItems.filter((item) => {
      const currentStock = item.currentStock || 0
      const minimumStock = item.minimumStock || 0
      return currentStock <= minimumStock
    })

    const expiringItems = filteredItems.filter((item) => {
      if (!item.expiryDate) return false
      const daysUntilExpiry = dayjs(item.expiryDate).diff(dayjs(), 'day')
      return daysUntilExpiry >= 0 && daysUntilExpiry <= 90
    })

    return {
      totalValue,
      totalItems: filteredItems.length,
      itemsNeedingReorder,
      expiringItems
    }
  }, [filteredItems])

  const categoryAnalysis = useMemo(() => {
    const analysis = filteredItems.reduce((acc, item) => {
      const category = item.category || 'Uncategorized'
      if (!acc[category]) {
        acc[category] = {
          name: category,
          itemCount: 0,
          totalValue: 0,
          totalStock: 0,
          items: []
        }
      }
      acc[category].itemCount += 1
      acc[category].totalValue += (item.currentStock || 0) * (item.unitPrice || 0)
      acc[category].totalStock += item.currentStock || 0
      acc[category].items.push(item)
      return acc
    }, {})

    return Object.values(analysis).map((category) => ({
      ...category,
      averageStock: Math.round(category.totalStock / category.itemCount),
      valuePercentage: ((category.totalValue / overviewData.totalValue) * 100).toFixed(1)
    }))
  }, [filteredItems, overviewData.totalValue])

  const expiryAnalysis = useMemo(() => {
    const groups = {
      critical: filteredItems.filter((item) => {
        const days = dayjs(item.expiryDate).diff(dayjs(), 'day')
        return days >= 0 && days <= 30
      }),
      warning: filteredItems.filter((item) => {
        const days = dayjs(item.expiryDate).diff(dayjs(), 'day')
        return days > 30 && days <= 60
      }),
      attention: filteredItems.filter((item) => {
        const days = dayjs(item.expiryDate).diff(dayjs(), 'day')
        return days > 60 && days <= 90
      })
    }

    return {
      groups,
      timelineItems: [
        {
          color: 'red',
          children: `Critical (≤ 30 days): ${groups.critical.length} items`
        },
        {
          color: 'orange',
          children: `Warning (31-60 days): ${groups.warning.length} items`
        },
        {
          color: 'blue',
          children: `Attention (61-90 days): ${groups.attention.length} items`
        }
      ]
    }
  }, [filteredItems])

  const turnoverAnalysis = useMemo(() => {
    // Calculate turnover rate for each item
    const analysis = filteredItems.map((item) => {
      const soldQuantity = item.totalSold || 0
      const averageInventory = (item.currentStock + (item.initialStock || item.currentStock)) / 2
      const turnoverRate = averageInventory > 0 ? soldQuantity / averageInventory : 0

      return {
        ...item,
        soldQuantity,
        averageInventory: Math.round(averageInventory),
        turnoverRate: Number(turnoverRate.toFixed(2)),
        turnoverHealth: turnoverRate >= 1.5 ? 'Healthy' : turnoverRate >= 0.8 ? 'Moderate' : 'Low'
      }
    })

    // Group by category and calculate category averages
    const categoryTurnover = Object.values(
      analysis.reduce((acc, item) => {
        const category = item.category || 'Uncategorized'
        if (!acc[category]) {
          acc[category] = {
            name: category,
            items: [],
            totalTurnover: 0,
            totalValue: 0,
            totalSold: 0,
            totalInventory: 0
          }
        }
        acc[category].items.push(item)
        acc[category].totalTurnover += item.turnoverRate
        acc[category].totalValue += (item.currentStock || 0) * (item.unitPrice || 0)
        acc[category].totalSold += item.soldQuantity
        acc[category].totalInventory += item.averageInventory
        return acc
      }, {})
    ).map((cat) => {
      const averageTurnover = Number((cat.totalTurnover / cat.items.length).toFixed(2))
      // Also calculate turnover using the aggregate method
      const categoryTurnoverRate = cat.totalInventory > 0 ? Number((cat.totalSold / cat.totalInventory).toFixed(2)) : 0

      return {
        ...cat,
        averageTurnover,
        turnoverRate: categoryTurnoverRate, // This is more accurate than averaging the rates
        itemCount: cat.items.length,
        turnoverHealth: categoryTurnoverRate >= 1.5 ? 'Healthy' : categoryTurnoverRate >= 0.8 ? 'Moderate' : 'Low'
      }
    })

    // Count items by turnover health
    const healthyCounts = analysis.filter((item) => item.turnoverHealth === 'Healthy').length
    const moderateCounts = analysis.filter((item) => item.turnoverHealth === 'Moderate').length
    const lowCounts = analysis.filter((item) => item.turnoverHealth === 'Low').length

    // Calculate overall average turnover rate
    const overallAverageTurnover =
      analysis.length > 0
        ? Number((analysis.reduce((sum, item) => sum + item.turnoverRate, 0) / analysis.length).toFixed(2))
        : 0

    return {
      items: analysis,
      categoryTurnover,
      healthyCounts,
      moderateCounts,
      lowCounts,
      overallAverageTurnover
    }
  }, [filteredItems])

  return {
    overviewData,
    categoryAnalysis,
    expiryAnalysis,
    turnoverAnalysis,
    filteredItems
  }
}

export default useReportsData
